<script setup lang="ts">
import type { Product, ProductCategory } from '~/types/product.type'

const props = defineProps<{
  options: ProductCategory[]
  modelValue: ProductCategory
  products: Product[]
}>()

const emits = defineEmits<{
  'update:modelValue': [ProductCategory | null]
}>()

const { t } = useI18n()

function chooseColor(color: ProductCategory) {
  if (props.modelValue?.id === color.id) {
    emits('update:modelValue', null)
    return
  }
  emits('update:modelValue', color)
}

const isOpen = ref(true)
const showAll = ref(false)
const defaultShowCount = 5

function getProductsWithFilter(color: ProductCategory) {
  return props.products.filter(product => product.product_category_id === color.id).length
}
</script>

<template>
  <div class="w-full border rounded-lg p-2">
    <button class="w-full text-left flex items-center justify-between text-lg rounded font-bold px-2" @click="isOpen = !isOpen">
      <p>
        {{ t('label.category') }}
      </p>
      <span
        class="i-mdi-chevron-down w-5 h-5 text-black duration-100" :class="{
          'transform rotate-180': isOpen,
        }"
      />
    </button>
    <div v-if="isOpen">
      <div class="flex flex-col gap-2 px-2 my-2">
        <template v-for="(option, index) in options" :key="option.measurement">
          <div v-if="index < defaultShowCount || showAll" class="flex overflow-hidden">
            <button class="flex items-center gap-2 w-full" @click="chooseColor(option)">
              <div class="h-5 w-5 flex items-center justify-center border-gray border rounded-full flex-none">
                <div
                  :class="{
                    'h-2 w-2': modelValue?.id === option.id,
                    'h-0 w-0': modelValue?.id !== option.id,
                  }" class="bg-primary rounded-full duration-300"
                />
              </div>

              <div class="flex items-center gap-2" style="width: calc(100% - 28px);">
                <span class="truncate">{{ option.title }}</span>
                <span class="text-gray text-sm flex-none">({{ getProductsWithFilter(option) }})</span>
              </div>
            </button>
          </div>
        </template>
      </div>
      <div v-if="options.length > defaultShowCount" class="flex justify-center text-primary">
        <button class="w-full text-left flex items-center rounded gap-2 px-2" @click="showAll = !showAll">
          <span
            class="i-mdi-chevron-down w-5 h-5 duration-300" :class="{
              'transform rotate-180': showAll,
            }"
          />
          <p>
            {{ showAll ? $t('label.show_less') : $t('label.show_all') }}
          </p>
        </button>
      </div>
    </div>
  </div>
</template>
